import { useState, useEffect } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { X, Check } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { useSelector } from 'react-redux';
import { selectToken } from 'slices/auth/login/reducer';
import { CallAPIPostPromise } from 'Common/commaon';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import {PERMISSIONS} from '../../Common/constants/permissions'
interface User {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  permissions: string[];
  role?: 'Owner' | 'Staff';
  active?: boolean;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?:any;
}

interface PermissionGroup {
  name: string;
  permissions: string[];
}

const permissionGroups: PermissionGroup[] = [
  {
    name: "Leads",
    permissions: [
      PERMISSIONS.VIEW_JOBS,
      PERMISSIONS.RESPOND_JOB,
    ],
  },
  {
    name: "Contracts",
    permissions: [
      PERMISSIONS.CREATE_CONTRACT,
      PERMISSIONS.VIEW_CONTRACT,
      PERMISSIONS.UPDATE_CONTRACT,
      PERMISSIONS.DELETE_CONTRACT,
      PERMISSIONS.SIGN_CONTRACT,
      PERMISSIONS.MANAGE_CONTRACT_MILESTONE,
      PERMISSIONS.MANAGE_CHANGE_ORDER,
      PERMISSIONS.MANAGE_SUBCONTRACT, // fixed typo
    ],
  },
  {
    name: "Company",
    permissions: [
      PERMISSIONS.VIEW_COMPANY_STAFF,
      PERMISSIONS.MANAGE_COMPANY_STAFF,
      PERMISSIONS.VIEW_COMPANY,
      PERMISSIONS.UPDATE_COMPANY,
      PERMISSIONS.DELETE_COMPANY,
    ],
  },
  {
    name: "Billing",
    permissions: [
      PERMISSIONS.MANAGE_BILLING,
    ],
  },

 
];

const allPermissions = permissionGroups.flatMap(group => group.permissions);

export function UserModal({ isOpen, onClose, user }: UserModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<User>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    permissions: [],
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.user_first_name,
        lastName: user.user_last_name,
        email: user.user_email,
        phone: user.user_phone || '',
        permissions: user.permissions || [],
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        permissions: [],
      });
    }
  }, [user, isOpen]);

  const togglePermission = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.length === allPermissions.length ? [] : [...allPermissions],
    }));
  };
  const token = useSelector(selectToken)
  let decoded ='' as any
if(token){ 
  decoded = jwtDecode(token)
}
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    let apiUrl ="/account/createCompanyUser"
  if(user?.id){
apiUrl ="/account/updateCompanyUser"
  }
    try {
      setIsSubmitting(true);
      await CallAPIPostPromise(apiUrl, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          gender:"male",
          companyId:decoded.companyId,
          phone: formData.phone,
          permissions: formData.permissions,
          isTerms: true,
          userId:user?.id||""
       
      }) as any
      toast.success(user?.id ? "Team member updated successfully" : "Team member added successfully");
      onClose();
  
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while saving the team member");
    } finally {
      setIsSubmitting(false);
    }


  };

  const isAllSelected = formData.permissions.length === allPermissions.length;

  return (
    <Dialog  open={isOpen}  onOpenChange={onClose}>
<DialogContent   style={{
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "white",
    border: "1px solid var(--border)",
    borderRadius: "12px",
    boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
    width: "100%",
    maxWidth: "42rem", // max-w-2xl
    maxHeight: "90vh",
    overflowY: "auto",
    zIndex: 10000,
    backgroundColor:'white',
    padding:0
  }} className='tailwind-prefix team-member-dialog'>
       <div
  className="team-modal-header"
  style={{
    position: "sticky",
    top: 0,
    background: "white",
    borderBottom: "1px solid gray",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 10
  }}
>
  <DialogTitle
    style={{
      color: "var(--card-foreground)",
      fontSize: "18px",
      fontWeight: 600,

    }}
  >
    {user ? "Edit User" : "Add New User"}
  </DialogTitle>

  <DialogClose
    style={{
      padding: "8px",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background 0.2s ease"
    }}
    className='bg-transparent border-0'
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = "var(--accent)")
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.background = "transparent")
    }
  >
    <X
      style={{
        width: "20px",
        height: "20px",
        color: "var(--muted-foreground)"
      }}
    />
  </DialogClose>
</div>

          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-6 team-modal-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-card-foreground block">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  disabled={isSubmitting}
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="lastName" className="text-card-foreground block">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  disabled={isSubmitting}
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-card-foreground block">
                Email
              </label>
              <input
                id="email"
                type="email"
                disabled={isSubmitting}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="phone" className="text-card-foreground block">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                disabled={isSubmitting}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-card-foreground">Permissions</label>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={toggleSelectAll}
                  className="text-sm bg-transparent border-0 text-primary hover:underline"
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-[14px] space-y-4 team-permission-groups">
                {permissionGroups.map((group) => (
                  <div key={group.name} className="space-y-3">
                    <div className="text-gray-500 text-[14px] font-[500]">{group.name}</div>
                    <div className="space-y-2">
                      {group.permissions.map((permission) => (
                        <label
                          key={permission}
                          className="flex items-center gap-3 cursor-pointer group"
                        >
                          <Checkbox.Root
                            disabled={isSubmitting}
                            checked={formData.permissions.includes(permission)}
                            onCheckedChange={() => togglePermission(permission)}
                            className="w-5 h-5 bg-input-background border border-border rounded flex items-center justify-center data-[state=checked]:bg-primary data-[state=checked]:border-primary transition-colors"
                          >
                            <Checkbox.Indicator>
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </Checkbox.Indicator>
                          </Checkbox.Root>
                          <span className="text-card-foreground group-hover:text-foreground transition-colors">
                            {permission}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-border">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex flex-1 items-center justify-center gap-2 border-0 px-6 py-2.5 text-white rounded-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70 team-modal-submit"
              >
                {isSubmitting && (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {isSubmitting ? (user ? 'Saving...' : 'Adding...') : (user ? 'Save Changes' : 'Add User')}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onClose}
                className="flex-1 border-0 bg-gray-100 px-6 py-2.5 bg-muted text-muted-foreground rounded-lg transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
       
    </Dialog>
  );
}
